import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Paper, Skeleton, Stack, Tooltip, Typography } from '@mui/material'

import {
  BID_ANALYSIS_CHART_HEIGHT,
  COLOR_ALL_JOBS,
  COLOR_CX_WON,
  COLOR_OP_WON,
} from 'components/reports/Metrics/constants'
import { BidsPerJobDto } from 'generated/graphql'

import { BucketBarChart } from '../components/BucketBarChart'

interface Props {
  data?: Pick<BidsPerJobDto, 'totalJobs' | 'jobsWithoutBids' | 'buckets'> | null
  loading?: boolean
}

export const BidsPerJobCard = ({ data, loading }: Props) => {
  const buckets = data?.buckets ?? []

  const series = [
    { label: 'All Jobs', color: COLOR_ALL_JOBS, values: buckets.map((b) => b.allJobs) },
    { label: 'CX Won Jobs', color: COLOR_CX_WON, values: buckets.map((b) => b.cxWonJobs) },
    {
      label: 'OnePoint Won Jobs',
      color: COLOR_OP_WON,
      values: buckets.map((b) => b.onePointWonJobs),
    },
  ]

  return (
    <Paper
      variant='outlined'
      sx={{ p: 2.5, borderRadius: 2, borderTop: `3px solid ${COLOR_ALL_JOBS}` }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        justifyContent='space-between'
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        mb={2}>
        <Stack direction='row' spacing={1} alignItems='center'>
          <Typography variant='subtitle1' fontWeight={700} sx={{ color: 'primary.main' }}>
            Number of Bids Received Per Job
          </Typography>
          <Tooltip title='Counts offers from both networks: OnePoint bids and Courier Exchange quotes. A job is won by the network whose offer was accepted — a bid in the Driver App, or an accepted Courier Exchange quote. Jobs not yet allocated, and jobs given to a supplier who did not make the winning offer, appear under All Jobs only, so the two won series add up to less than All Jobs.'>
            <InfoOutlinedIcon fontSize='small' sx={{ color: 'text.disabled' }} />
          </Tooltip>
        </Stack>

        <Stack alignItems={{ xs: 'flex-start', sm: 'flex-end' }}>
          <Typography variant='caption' color='text.secondary'>
            Jobs with at least one bid
          </Typography>
          {loading ? (
            <Skeleton width={60} height={32} />
          ) : (
            <Typography variant='h6' fontWeight={700}>
              {(data?.totalJobs ?? 0).toLocaleString()}
            </Typography>
          )}
        </Stack>
      </Stack>

      {loading ? (
        <Skeleton
          variant='rectangular'
          height={BID_ANALYSIS_CHART_HEIGHT}
          sx={{ borderRadius: 1 }}
        />
      ) : (
        <BucketBarChart
          labels={buckets.map((b) => b.label)}
          series={series}
          xAxisLabel='Number of Bids Per Job'
        />
      )}

      <Typography variant='caption' color='text.secondary' display='block' mt={1}>
        {(data?.jobsWithoutBids ?? 0).toLocaleString()} posted job(s) received no bids and are not
        shown, as the chart starts at one bid.
      </Typography>
    </Paper>
  )
}
